import { Link } from "react-router-dom";
import { Ship, Train, Anchor, ArrowRight, Globe2, MapPin } from "lucide-react";
import { FREIGHT_ROUTES, GLOBAL_HUBS, COVERAGE_REGIONS } from "@/data/freightRoutes";
import FreightQuoteForm from "@/components/FreightQuoteForm";

const FreightQuote = () => {
  return (
    <div className="bg-[var(--tt-cream)] min-h-screen">
      <section className="relative overflow-hidden border-b border-[var(--tt-line)]">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--tt-navy)] via-[var(--tt-navy-2)] to-[var(--tt-green)] opacity-95" />
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div className="relative mx-auto max-w-7xl px-6 py-14 md:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-3 py-1 text-xs font-semibold text-white uppercase tracking-widest mb-4">
              <Globe2 className="h-3.5 w-3.5" /> Global freight
            </div>
            <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight">
              Global ocean &amp; rail freight — <span className="text-[var(--tt-accent-2)]">quoted in one business day</span>.
            </h1>
            <p className="text-white/85 text-base md:text-lg mt-5 max-w-2xl">
              FCL, LCL, or trans-Eurasian rail — moving cargo across 6 continents and 20,000+
              door-to-door destinations. Tell us the route and cargo, we lock in space with top
              carriers and reply with a firm rate.
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-white/80 text-sm">
              <div className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-accent-2)]" /> 6 continents</div>
              <div className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-accent-2)]" /> 20,000+ destinations</div>
              <div className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-accent-2)]" /> 30+ EU rail countries</div>
              <div className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-accent-2)]" /> 5 regional hubs</div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 items-start">
          <div>
            <FreightQuoteForm />
          </div>
          <div className="space-y-4">
            <div className="rounded-3xl border border-[var(--tt-line)] bg-white p-6">
              <div className="text-[11px] uppercase tracking-widest text-[var(--tt-green)] font-semibold">Popular lanes</div>
              <h3 className="font-display font-black text-2xl text-[var(--tt-navy)] tracking-tight mt-1 mb-4">
                Jump to a dedicated route
              </h3>
              <div className="space-y-2">
                {Object.values(FREIGHT_ROUTES).map((r) => (
                  <Link key={r.slug} to={`/freight/${r.slug}`} data-testid={`freight-quote-lane-${r.slug}`} className="group flex items-center justify-between rounded-2xl border border-[var(--tt-line)] bg-[var(--tt-cream)] p-4 hover:border-[var(--tt-green)] hover:bg-white transition">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[var(--tt-green)] font-semibold">{r.hero.eyebrow}</div>
                      <div className="font-semibold text-[var(--tt-navy)] text-sm mt-0.5">{r.hero.title.slice(0, 55)}…</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-[var(--tt-green)] group-hover:translate-x-1 transition-transform" />
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-[var(--tt-line)] bg-white p-6">
              <div className="text-[11px] uppercase tracking-widest text-[var(--tt-green)] font-semibold">Modes we run</div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="rounded-2xl bg-[var(--tt-mint-soft)] p-3 text-center">
                  <Ship className="h-5 w-5 text-[var(--tt-green)] mx-auto" />
                  <div className="text-xs font-bold text-[var(--tt-navy)] mt-1">FCL</div>
                  <div className="text-[10px] text-[var(--tt-muted)]">Your container</div>
                </div>
                <div className="rounded-2xl bg-[var(--tt-mint-soft)] p-3 text-center">
                  <Anchor className="h-5 w-5 text-[var(--tt-green)] mx-auto" />
                  <div className="text-xs font-bold text-[var(--tt-navy)] mt-1">LCL</div>
                  <div className="text-[10px] text-[var(--tt-muted)]">Pay per CBM</div>
                </div>
                <div className="rounded-2xl bg-[var(--tt-mint-soft)] p-3 text-center">
                  <Train className="h-5 w-5 text-[var(--tt-green)] mx-auto" />
                  <div className="text-xs font-bold text-[var(--tt-navy)] mt-1">Rail</div>
                  <div className="text-[10px] text-[var(--tt-muted)]">CN ↔ EU</div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-[var(--tt-line)] bg-[var(--tt-navy)] text-white p-6">
              <div className="text-[11px] uppercase tracking-widest text-[var(--tt-accent-2)] font-semibold">Regional hubs</div>
              <h3 className="font-display font-black text-2xl tracking-tight mt-1 mb-4">
                Where our network operates
              </h3>
              <ul className="space-y-2 text-sm">
                {GLOBAL_HUBS.map((h) => (
                  <li key={h.city} className="flex items-center justify-between border-b border-white/10 pb-2 last:border-0">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5 text-[var(--tt-accent-2)]" />
                      <span className="font-semibold">{h.city}</span>
                      <span className="text-white/60 text-xs">· {h.region}</span>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-white/70">{h.role}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Global Coverage */}
      <section className="mx-auto max-w-7xl px-6 pb-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-[11px] uppercase tracking-widest text-[var(--tt-green)] font-semibold">Global coverage</div>
          <h2 className="font-display font-black text-[var(--tt-navy)] text-3xl md:text-4xl tracking-tight mt-1">
            Six continents. One freight desk.
          </h2>
          <p className="text-[var(--tt-muted)] text-sm md:text-base mt-3">
            A snapshot of destination countries we regularly move ocean and rail freight to.
            Don't see yours? Ask us — with 20,000+ door-to-door destinations, chances are we're
            already there.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="freight-coverage-grid">
          {COVERAGE_REGIONS.map((r) => (
            <div key={r.key} data-testid={`freight-coverage-${r.key}`} className="rounded-2xl border border-[var(--tt-line)] bg-white p-5 hover:border-[var(--tt-green)] transition">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-display font-black text-xl text-[var(--tt-navy)] tracking-tight">{r.name}</h3>
                <Globe2 className="h-5 w-5 text-[var(--tt-green)] shrink-0" />
              </div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {r.lanes.map((l) => (
                  <span key={l} className="text-[10px] font-semibold text-[var(--tt-green)] bg-[var(--tt-mint-soft)] px-2 py-1 rounded-full">
                    {l}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {r.countries.map((c) => (
                  <span key={c} className="text-xs text-[var(--tt-ink-soft)] bg-[var(--tt-cream)] px-2.5 py-1 rounded-md border border-[var(--tt-line)]">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-3xl bg-gradient-to-br from-[var(--tt-navy)] via-[var(--tt-navy-2)] to-[var(--tt-green)] text-white p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[var(--tt-accent-2)] font-semibold">Don't see your country?</div>
            <h3 className="font-display font-black text-2xl md:text-3xl mt-1 tracking-tight">
              We probably still ship there.
            </h3>
            <p className="text-white/80 text-sm md:text-base mt-2 max-w-lg">
              20,000+ door-to-door destinations worldwide. Send us a route and we'll come back
              with a firm rate and transit time within one business day.
            </p>
          </div>
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="freight-coverage-cta" className="inline-flex items-center gap-2 rounded-full bg-white text-[var(--tt-navy)] font-bold px-6 py-3 text-sm shadow-lg hover:bg-[var(--tt-accent-2)] transition whitespace-nowrap">
            Request a quote <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default FreightQuote;
